/**
 * WHICH relationships this user actually opens — per user, on the server.
 *
 * Christopher's decision, 2026-08-06, on ordering the relationship picker:
 * non-empty first, then alphabetical, "overridden by most frequently used by
 * the user", and the frequency is stored **server-side per user** — not in
 * localStorage. A picker that reorders itself on one machine and not the other
 * is a picker the user cannot learn.
 *
 * `core.user_setting` is the store, reached through the same `my_settings()` /
 * `set_user_setting()` pair `data/userFormat.ts` and `list/useColumnWidths.ts`
 * use. **No migration is needed** — the table is a per-user key/value store with
 * its own RLS, and a new key is a new key.
 *
 *   key    `relationship_frequency`
 *   shape  `{ "<from_entity>": { "<slot_id>": <count> } }`
 *
 * Keyed by SLOT id (`works_at:in:core_person`), not by relationship key: the two
 * directions of one type are two different things to open, and a user who lives
 * in "People of this organization" has said nothing about "Organizations this
 * person works at".
 *
 * ── It is the FALLBACK now, not the answer (#1466 B4) ──────────────────────
 * A count cannot say "pin this", "hide that", or "this one first even though I
 * rarely open it", so `related_order` (`useRelatedOrder.ts`) carries the STATED
 * rung and `orderSlots` applies it above this one. Nothing here changed: the
 * learned signal still decides every slot the person has said nothing about,
 * which for anybody who has not opened the editor is all of them.
 *
 * ── Why the writes are not debounced, and why that is fine ─────────────────
 * A count is bumped when a slot is CHOSEN — a deliberate click, at human speed,
 * not a drag. `useColumnWidths` debounces because a resize emits a write per
 * animation frame; this cannot. What it does do is fire-and-forget: an ordering
 * hint that fails to save must never surface an error over a list that opened
 * correctly.
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSolum } from '../data/client'

const KEY = 'relationship_frequency'
/** `{ entity: { slotId: count } }` */
type FreqMap = Record<string, Record<string, number>>

export interface SlotFrequency {
  /** how many times this user has opened that slot — 0 when never, or unknown */
  countOf: (slotId: string) => number
  /** record a choice; fire-and-forget */
  bump: (slotId: string) => void
}

export function useSlotFrequency(entity: string): SlotFrequency {
  const { client } = useSolum()
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    staleTime: 60_000,
  })

  const mine = useMemo(
    () => ((q.data?.[KEY] ?? {}) as FreqMap)[entity] ?? {},
    [q.data, entity])

  const save = useMutation({
    mutationFn: (next: Record<string, number>) => {
      const all = (q.data?.[KEY] ?? {}) as FreqMap
      return client.raw().rpcScalar('set_user_setting', {
        p_key: KEY, p_value: { ...all, [entity]: next },
      })
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['user-settings'] }) },
  })

  const bump = useCallback((slotId: string) => {
    if (!entity || !slotId) return
    // A REFUSED or still-loading settings read means the counts are unknown, and
    // writing `{slot: 1}` over an unread map would erase every other count this
    // user has. Skipping the bump loses one increment; the alternative loses the
    // history. `q.isSuccess` is the only state in which the base map is known.
    if (!q.isSuccess) return
    save.mutate({ ...mine, [slotId]: (mine[slotId] ?? 0) + 1 })
  }, [entity, mine, q.isSuccess, save])

  return {
    countOf: useCallback((slotId: string) => mine[slotId] ?? 0, [mine]),
    bump,
  }
}

/**
 * The picker's order, as a pure function — so it is testable without a browser,
 * a server or a click.
 *
 *   0. what THIS USER SAID, if they said anything about this slot (#1466 B4).
 *   1. what the governed FORM declared important, if it named this slot.
 *   2. slots that HAVE something, before slots known to be empty. An unknown
 *      count stays on the populated side of that line (#389).
 *   3. the BIGGEST first — which lands an unknown between the counted and the
 *      known-empty, because an unknown has no size of its own (#2000).
 *   4. most-opened by THIS user first — only between slots of equal size.
 *   5. then the localized label.
 *   6. then the slot id, so the answer is stable when everything above ties.
 *
 * An UNKNOWN count (the server derived none, or the read was refused) is not
 * zero — see `useRelatedCounts`. It sorts with the non-empty group, because
 * demoting a slot whose rows RLS merely declined to count would hide it.
 *
 * ── Why the STATED arm is above `populated`, not beside frequency ──────────
 * Rule 0 was added by #1466 B4 and it is the top term on purpose. A frequency
 * is a count this console derived by watching; a stated position is a sentence
 * the person typed with their hands. "Populated outranks frequency" is the right
 * call between two things the machine inferred — a chooser that opens onto
 * nothing because of HISTORY is a defect — but it is the wrong call against an
 * instruction: a person who drags an empty relationship to the top has said
 * *put it there*, usually because that is where they are about to create the
 * first row.
 *
 * `statedOf` returns `null` for a slot the person never mentioned, and `null` is
 * NOT "last". It falls through to rules 1-3 unchanged, which is what keeps the
 * learned signal alive for everything the statement is silent about — demoted,
 * never deleted. A person who has never opened the editor gets a `statedOf` that
 * answers `null` to everything and therefore the order they had before, to the
 * row.
 *
 * The parameter is REQUIRED rather than defaulted: a call site that forgets it
 * silently discards the person's statement and nothing on screen says so, which
 * is the one failure mode this arm cannot afford. `useRelatedOrder.groupSlots`
 * is the only caller in the app, and it is where the store is read.
 */
export function orderSlots<T extends { id: string; label: string }>(
  slots: T[],
  countOf: (id: string) => number | null,
  frequencyOf: (id: string) => number,
  statedOf: (id: string) => number | null,
  declaredOf: (id: string) => number | null,
): T[] {
  /* KNOWN-EMPTY last. An unknown stays on the populated side of this line,
     exactly as before (#389): a slot whose rows RLS declined to count must
     never be demoted to sit with the empties. */
  const populated = (s: T) => {
    const n = countOf(s.id)
    return n === null || n > 0 ? 1 : 0
  }
  /* Size, descending — and this is what makes the order three-state rather
     than two, with no third rank needed. An unknown counts as 0 here, so every
     slot that WAS counted sorts above it, while `populated` still keeps it
     clear of the known-empties. positive → unknown → zero falls out of the two
     terms together.

     That redundancy was measured, not assumed. A third rank placing unknown
     strictly between positive and zero was written first, and the mutation
     collapsing it back into `populated` left the suite GREEN — `size` already
     decides every pair the extra rank claimed to. Two terms that produce the
     answer beat three where one of them is inert.

     A CAPPED count (`99+`, `useRelatedCounts`) ties with an exact count of the
     same n on purpose. The cap is the only number the server produced; ordering
     it above an exact 99 would rank a slot by a quantity nobody measured, which
     is the same error as drawing an unknown as a zero, pointed the other way. */
  const size = (s: T) => countOf(s.id) ?? 0
  /* Unmentioned sorts after every mentioned slot AND ties with every other
     unmentioned one — the tie is the point, because it is what lets rules 1-3
     decide between them. */
  const stated = (s: T) => statedOf(s.id) ?? Number.MAX_SAFE_INTEGER
  const declared = (s: T) => declaredOf(s.id) ?? Number.MAX_SAFE_INTEGER
  return [...slots].sort((a, b) =>
    stated(a) - stated(b)
    || declared(a) - declared(b)
    || populated(b) - populated(a)
    || size(b) - size(a)
    || frequencyOf(b.id) - frequencyOf(a.id)
    || a.label.localeCompare(b.label)
    || a.id.localeCompare(b.id))
}
